import {parseNumber} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React from 'react';

import {assert} from 'Asserts';
import {StringValueDropdownSelector} from 'insights/analytics-visualizations/configs/form-components/DropdownSelector';
import getTestProps from 'test/lib/cypress/getTestProps';
import FieldError from 'ui-library/FieldError';
import {IntermediateValueControlledNumericInput} from 'ui-library/inputs/ControlledNumericInput';
import {assertNever} from 'util/Types';

import {RegressionParameterConstraintType, RegressionParameterName} from '../RegressionTypes';
import {
  CUSTOM_INPUT_PROPERTIES,
  EMPTY_NUMERIC_INPUT_VALUE,
  PARAMETER_NAME_TO_READABLE,
} from './RegressionConfigModalConstants';
import {
  getParameterConstraintInputStates,
  getParameterValueErrorMessage,
} from './regressionConfigModalStateIsValid';
import {
  RegressionConfigModalParameterBetweenConstraint,
  RegressionConfigModalParameterConstraint,
} from './RegressionConfigModalTypes';

export const CONSTRAINT_TYPE_TO_READABLE: {[ConstraintType in RegressionParameterConstraintType]: string} = {
  [RegressionParameterConstraintType.NONE]: 'None',
  [RegressionParameterConstraintType.EQUAL]: 'Is equal to',
  [RegressionParameterConstraintType.LESS_THAN]: 'Is less than',
  [RegressionParameterConstraintType.GREATER_THAN]: 'Is greater than',
  [RegressionParameterConstraintType.BETWEEN]: 'Is between',
};

export const CONSTRAINT_TYPE_TO_EMPTY_VALUE: {
  [ConstraintType in RegressionParameterConstraintType]: RegressionConfigModalParameterConstraint;
} = {
  [RegressionParameterConstraintType.NONE]: {
    type: RegressionParameterConstraintType.NONE,
  },
  [RegressionParameterConstraintType.EQUAL]: {
    type: RegressionParameterConstraintType.EQUAL,
    value: EMPTY_NUMERIC_INPUT_VALUE,
  },
  [RegressionParameterConstraintType.LESS_THAN]: {
    type: RegressionParameterConstraintType.LESS_THAN,
    upperBound: EMPTY_NUMERIC_INPUT_VALUE,
  },
  [RegressionParameterConstraintType.GREATER_THAN]: {
    type: RegressionParameterConstraintType.GREATER_THAN,
    lowerBound: EMPTY_NUMERIC_INPUT_VALUE,
  },
  [RegressionParameterConstraintType.BETWEEN]: {
    type: RegressionParameterConstraintType.BETWEEN,
    upperBound: EMPTY_NUMERIC_INPUT_VALUE,
    lowerBound: EMPTY_NUMERIC_INPUT_VALUE,
  },
};

export function getBetweenConstraintError({
  lowerBound,
  upperBound,
}: RegressionConfigModalParameterBetweenConstraint): string | null {
  const numericUpperBound = parseNumber(upperBound.value);
  if (
    lowerBound.numericValue != null &&
    numericUpperBound != null &&
    numericUpperBound <= lowerBound.numericValue
  ) {
    return `Upper bound must be greater than ${lowerBound.numericValue}`;
  } else {
    return null;
  }
}

export function getConstraintErrorMessage(
  parameterName: RegressionParameterName,
  constraint: RegressionConfigModalParameterConstraint
): string | null {
  const inputStates = getParameterConstraintInputStates(constraint);
  const errorMessage = inputStates.reduce(
    (errorMessage, inputState) =>
      errorMessage != null ? errorMessage : getParameterValueErrorMessage(parameterName, inputState.value),
    null
  );
  if (errorMessage != null) {
    return errorMessage;
  }
  if (constraint.type === RegressionParameterConstraintType.BETWEEN) {
    return getBetweenConstraintError(constraint);
  }
  return null;
}

export function ParameterConstraintNumericInputs({
  constraint,
  onChange,
  parameterName,
  className,
}: RegressionParameterConstraintEditorProps & {className: string}): JSX.Element | null {
  const sharedProps = {
    className,
    min: -Number.MAX_VALUE,
    max: Number.MAX_VALUE,
    ...CUSTOM_INPUT_PROPERTIES[parameterName],
  };

  switch (constraint.type) {
    case RegressionParameterConstraintType.NONE:
      return null;
    case RegressionParameterConstraintType.EQUAL:
      return (
        <IntermediateValueControlledNumericInput
          {...sharedProps}
          onChange={(e) => {
            onChange({
              ...constraint,
              value: e.target,
            });
          }}
          value={constraint.value.value}
        />
      );
    case RegressionParameterConstraintType.LESS_THAN:
      return (
        <IntermediateValueControlledNumericInput
          {...sharedProps}
          onChange={(e) => {
            onChange({
              ...constraint,
              upperBound: e.target,
            });
          }}
          value={constraint.upperBound.value}
        />
      );
    case RegressionParameterConstraintType.GREATER_THAN:
      return (
        <IntermediateValueControlledNumericInput
          {...sharedProps}
          onChange={(e) => {
            onChange({
              ...constraint,
              lowerBound: e.target,
            });
          }}
          value={constraint.lowerBound.value}
        />
      );
    case RegressionParameterConstraintType.BETWEEN: {
      const {upperBound, lowerBound} = constraint;
      return (
        <>
          <IntermediateValueControlledNumericInput
            {...sharedProps}
            onChange={(e) => {
              onChange({
                ...constraint,
                lowerBound: e.target,
              });
            }}
            value={lowerBound.value}
          />
          <IntermediateValueControlledNumericInput
            {...sharedProps}
            onChange={(e) => {
              onChange({
                ...constraint,
                upperBound: e.target,
              });
            }}
            value={upperBound.value}
          />
        </>
      );
    }
    default:
      assert(false, `Unexpected constraint ${assertNever(constraint)}`, constraint);
  }
}

export interface RegressionParameterConstraintEditorProps {
  parameterName: RegressionParameterName;
  constraint: RegressionConfigModalParameterConstraint;
  onChange: (newValue: RegressionConfigModalParameterConstraint) => void;
  wrapperClassName?: string;
  labelClassName?: string;
  typeSelectorClassName?: string;
  inputsWrapperClassName?: string;
  inputClassName?: string;
}

export default function RegressionParameterConstraintEditor(
  props: RegressionParameterConstraintEditorProps
): JSX.Element {
  const {
    wrapperClassName,
    labelClassName,
    typeSelectorClassName,
    inputsWrapperClassName,
    inputClassName,
    ...otherProps
  } = props;
  return (
    <>
      <div
        className={wrapperClassName ?? classNames(css(styles.wrapper), 'u-vertical-center-items')}
        {...getTestProps({
          component: 'RegressionParameterConstraintEditor',
          element: 'root',
          key: props.parameterName,
        })}
      >
        <div
          className={labelClassName ?? classNames(css(styles.label), 'u-spacing-right')}
          {...getTestProps({
            component: 'RegressionParameterConstraintEditor',
            element: 'label',
            key: props.parameterName,
          })}
        >
          {PARAMETER_NAME_TO_READABLE[props.parameterName]}
        </div>
        <div className={typeSelectorClassName ?? classNames(css(styles.typeSelector), 'u-spacing-right')}>
          <StringValueDropdownSelector
            allowNull={false}
            onChange={(type: RegressionParameterConstraintType) => {
              props.onChange(CONSTRAINT_TYPE_TO_EMPTY_VALUE[type]);
            }}
            selectedValue={props.constraint.type}
            values={[
              RegressionParameterConstraintType.NONE,
              RegressionParameterConstraintType.EQUAL,
              RegressionParameterConstraintType.LESS_THAN,
              RegressionParameterConstraintType.GREATER_THAN,
              RegressionParameterConstraintType.BETWEEN,
            ]}
            searchable={false}
            valueToReadable={(value) => CONSTRAINT_TYPE_TO_READABLE[value]}
          />
        </div>
        <div className={inputsWrapperClassName ?? css(styles.numericInputsWrapper)}>
          <ParameterConstraintNumericInputs
            {...otherProps}
            className={inputClassName ?? classNames(css(styles.numericInput), 'u-spacing-right')}
          />
        </div>
      </div>
      <FieldError message={getConstraintErrorMessage(props.parameterName, props.constraint)} />
    </>
  );
}

const styles = StyleSheet.create({
  wrapper: {
    display: 'flex',
  },
  label: {
    width: 45,
    fontWeight: 'bold',
  },
  typeSelector: {
    width: 150,
  },
  numericInputsWrapper: {
    flexShrink: 1,
  },
  numericInput: {
    width: 120,
  },
});
